import React, { useState } from 'react';
import {
  FaRupeeSign,
  FaHome,
  FaCalendar,
  FaCity,
  FaInfoCircle,
  FaBuilding,
  FaMoneyBillWave,
  FaChartLine,
  FaQuestionCircle
} from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const HRAExemptionCalculator = () => {
  const [basicSalary, setBasicSalary] = useState('');
  const [hraAmount, setHRAAmount] = useState('');
  const [rentPaid, setRentPaid] = useState('');
  const [cityType, setCityType] = useState('');
  const [exemption, setExemption] = useState('0.00');
  const [error, setError] = useState('');

  const calculateExemption = () => {
    if (!basicSalary || !hraAmount || !rentPaid || !cityType) {
      setError('Please fill in all fields');
      return;
    }

    const basic = parseFloat(basicSalary);
    const hra = parseFloat(hraAmount);
    const rent = parseFloat(rentPaid);

    if (isNaN(basic) || isNaN(hra) || isNaN(rent)) {
      setError('Please enter valid numbers');
      return;
    }

    // Calculate HRA exemption
    const actualHRA = hra;
    const rentExcess = rent - (basic * 0.1);
    let maxHRA;
    switch (cityType) {
      case 'metro':
        maxHRA = basic * 0.5;
        break;
      case 'special':
        maxHRA = basic * 0.45;
        break;
      case 'small':
        maxHRA = basic * 0.35;
        break;
      default:
        maxHRA = basic * 0.4;
    }

    const exemptionAmount = Math.min(actualHRA, rentExcess, maxHRA);
    setExemption(exemptionAmount.toFixed(2));
    setError('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getChartData = () => {
    const basic = parseFloat(basicSalary) || 0;
    const hra = parseFloat(hraAmount) || 0;
    const rent = parseFloat(rentPaid) || 0;
    const rentExcess = rent - (basic * 0.1);
    let maxHRA;
    switch (cityType) {
      case 'metro':
        maxHRA = basic * 0.5;
        break;
      case 'special':
        maxHRA = basic * 0.45;
        break;
      case 'small':
        maxHRA = basic * 0.35;
        break;
      default:
        maxHRA = basic * 0.4;
    }

    return [
      { name: 'Actual HRA', value: hra },
      { name: 'Rent Excess', value: rentExcess },
      { name: 'Max HRA', value: maxHRA },
      { name: 'Exemption', value: parseFloat(exemption) }
    ];
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-100 min-h-screen p-8">
      <Helmet>
        <title>HRA Exemption Calculator</title>
        <meta name="description" content="Calculate your HRA tax exemption amount based on your salary, HRA, and rent paid." />
        <meta property="og:title" content="HRA Exemption Calculator" />
        <meta property="og:description" content="Calculate your HRA tax exemption amount based on your salary, HRA, and rent paid." />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <h2 className="text-4xl font-bold text-gray-800 flex items-center">
            <FaHome className="mr-3 text-4xl text-indigo-600" />
            HRA Exemption Calculator
          </h2>
          <button 
            className="group relative text-indigo-600 hover:text-indigo-700 transition-colors duration-300"
            title="Help"
          >
            <FaInfoCircle className="w-7 h-7" />
            <span className="absolute -top-1 -right-1 bg-indigo-500 text-white px-2 py-1 text-xs rounded-full group-hover:scale-110 transition-transform duration-300">
              ?
            </span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-indigo-700 text-sm font-bold mb-3">
              <FaMoneyBillWave className="inline-block mr-2 text-indigo-500" />
              Basic Salary (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400" />
              <input
                type="number"
                value={basicSalary}
                onChange={(e) => setBasicSalary(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300"
                placeholder="Enter basic salary"
                min="0"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-indigo-700 text-sm font-bold mb-3">
              <FaMoneyBillWave className="inline-block mr-2 text-indigo-500" />
              HRA Amount (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400" />
              <input
                type="number"
                value={hraAmount}
                onChange={(e) => setHRAAmount(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300"
                placeholder="Enter HRA amount"
                min="0"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-indigo-700 text-sm font-bold mb-3">
              <FaBuilding className="inline-block mr-2 text-indigo-500" />
              Rent Paid (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400" />
              <input
                type="number"
                value={rentPaid}
                onChange={(e) => setRentPaid(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300"
                placeholder="Enter rent paid"
                min="0"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-indigo-700 text-sm font-bold mb-3">
              <FaCity className="inline-block mr-2 text-indigo-500" />
              City Type
            </label>
            <select
              value={cityType}
              onChange={(e) => setCityType(e.target.value)}
              className="w-full p-3 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300"
            >
              <option value="">Select city type</option>
              <option value="metro">Metro City (50% of Basic)</option>
              <option value="non-metro">Non-Metro City (40% of Basic)</option>
              <option value="special">Special Category City (45% of Basic)</option>
              <option value="small">Small Town (35% of Basic)</option>
            </select>
          </div>
        </div>

        <button
          onClick={calculateExemption}
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-green-600 font-bold py-4 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 mb-10"
        >
          <FaChartLine className="inline-block mr-2" />
          Calculate Exemption
        </button>

        <div className="space-y-8">
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaMoneyBillWave className="mr-3 text-indigo-600" />
              HRA Exemption Details
            </h2>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg mb-6">
                {error}
              </div>
            )}
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">HRA Exemption Amount</span>
                <span className="font-semibold text-indigo-600">
                  {formatCurrency(parseFloat(exemption))}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Actual HRA</span>
                <span className="font-semibold text-gray-600">
                  {formatCurrency(parseFloat(hraAmount))}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Rent Excess (Rent - 10% of Basic)</span>
                <span className="font-semibold text-gray-600">
                  {formatCurrency(parseFloat(rentPaid) - (parseFloat(basicSalary) * 0.1))}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Maximum HRA (Based on City)</span>
                <span className="font-semibold text-gray-600">
                  {cityType === 'metro' 
                    ? formatCurrency(parseFloat(basicSalary) * 0.5)
                    : cityType === 'special'
                    ? formatCurrency(parseFloat(basicSalary) * 0.45)
                    : cityType === 'small'
                    ? formatCurrency(parseFloat(basicSalary) * 0.35)
                    : formatCurrency(parseFloat(basicSalary) * 0.4)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartLine className="mr-3 text-indigo-600" />
              HRA Exemption Breakdown
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="value" stroke="#8884d8" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaQuestionCircle className="mr-3 text-indigo-600" />
              How to Use
            </h2>
            <ol className="list-decimal list-inside space-y-6">
              <li className="flex items-start">
                <span className="font-semibold mr-2">1. Enter Basic Salary:</span>
                <span className="text-gray-600">Input your monthly basic salary amount</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">2. Enter HRA Amount:</span>
                <span className="text-gray-600">Input the House Rent Allowance amount received</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">3. Enter Rent Paid:</span>
                <span className="text-gray-600">Input the actual rent paid for your accommodation</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">4. Select City Type:</span>
                <span className="text-gray-600">Choose your city category from the dropdown</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">5. Calculate:</span>
                <span className="text-gray-600">Click the Calculate button to get your HRA exemption amount</span>
              </li>
            </ol>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaMoneyBillWave className="mr-3 text-indigo-600" />
              Benefits of HRA Exemption
            </h2>
            <ul className="list-disc list-inside space-y-4">
              <li className="flex items-start">
                <span className="font-semibold mr-2">Tax Savings:</span>
                <span className="text-gray-600">HRA exemption reduces your taxable income</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">Flexible Location:</span>
                <span className="text-gray-600">Different exemption percentages for different city types</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">Multiple Components:</span>
                <span className="text-gray-600">Based on actual HRA, rent paid, and city type</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">Easy Calculation:</span>
                <span className="text-gray-600">Simplifies complex tax calculations</span>
              </li>
            </ul>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaQuestionCircle className="mr-3 text-indigo-600" />
              FAQ
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold flex items-center">
                  <span className="mr-2">1.</span>
                  What is HRA?
                </h3>
                <p className="text-gray-600">
                  HRA (House Rent Allowance) is a component of salary provided by employers to employees for accommodation expenses.
                </p>
              </div>
              <div>
                <h3 className="font-semibold flex items-center">
                  <span className="mr-2">2.</span>
                  How is HRA exemption calculated?
                </h3>
                <p className="text-gray-600">
                  HRA exemption is the minimum of three amounts: Actual HRA received, Rent paid minus 10% of basic salary, and Maximum HRA based on city type.
                </p>
              </div>
              <div>
                <h3 className="font-semibold flex items-center">
                  <span className="mr-2">3.</span>
                  What are the different city types?
                </h3>
                <p className="text-gray-600">
                  Metro (50%), Special Category (45%), Non-Metro (40%), and Small Town (35%) cities have different exemption percentages.
                </p>
              </div>
              <div>
                <h3 className="font-semibold flex items-center">
                  <span className="mr-2">4.</span>
                  Can I claim HRA exemption if I own a house?
                </h3>
                <p className="text-gray-600">
                  No, HRA exemption is only applicable if you're paying rent for your accommodation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRAExemptionCalculator;
